import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { FALLBACK_VIEWER_MILESTONES, computeMilestones } from "../../lib/priceEngine";
import AdminShell from "../../components/AdminShell";
import MilestoneStepper from "../../components/MilestoneStepper";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

// Season One category schedule — must match backend/core/season.py
const SEASON_START_DATE = "2026-08-15";
const SEASON_ONE_CATEGORIES = [
  "Tech", "Home", "Kitchen", "Grooming", "Fitness",
  "Audio", "Travel", "Wellness", "Desk", "Style",
  "Kids", "Outdoors", "Reading", "Craft", "Mega Finale",
];

function seasonSlotFor(scheduledLocal) {
  if (!scheduledLocal) return null;
  const sel = new Date(scheduledLocal);
  if (Number.isNaN(sel.getTime())) return null;
  // Interpret sel as an IST wall-clock instant → shift to a UTC-anchored date
  // that reflects the IST calendar day, then compare to the fixed IST start date.
  const iso = new Date(sel.getTime() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const startIso = SEASON_START_DATE; // already "2026-08-15" — no TZ conversion needed
  const dayDelta = Math.round(
    (new Date(iso + "T00:00:00Z").getTime() - new Date(startIso + "T00:00:00Z").getTime()) / 86400000
  );
  if (dayDelta < 0 || dayDelta >= 15) return null;
  return { drop_no: dayDelta + 1, category: SEASON_ONE_CATEGORIES[dayDelta] };
}

export default function AdminLives() {
  const [lives, setLives] = useState([]);
  const [products, setProducts] = useState([]);
  const [cohort, setCohort] = useState(null); // { cohort_key, cohort_label, cohort_emoji, cohort_cap, active_audience, thresholds }
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all"); // all | handpicked | vault

  const load = async () => {
    const [lr, pr, cr] = await Promise.all([
      adminApi.get("/lives"),
      adminApi.get("/products"),
      adminApi.get("/price-engine/cohort").catch(() => ({ data: null })),
    ]);
    setLives(lr.data);
    setProducts(pr.data);
    if (cr.data) setCohort(cr.data);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this live?")) return;
    await adminApi.delete(`/lives/${id}`);
    toast.success("Deleted");
    load();
  };

  const setStatus = async (live_id, action) => {
    try {
      await adminApi.post(`/lives/${live_id}/${action}`);
      toast.success(`Live ${action}ed`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const visible = lives.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (sourceFilter === "vault" && !l.auto_promoted) return false;
    if (sourceFilter === "handpicked" && l.auto_promoted) return false;
    return true;
  });

  return (
    <AdminShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="brand-tag">// Lives</div>
          <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Drops</h1>
        </div>
        <Button
          onClick={() => { setEdit(null); setOpen(true); }}
          className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11 px-6"
          data-testid="new-live-btn"
        >
          + New Drop
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="inline-flex border border-[#E5E1D8] bg-[#FFFFFF] p-1" data-testid="live-status-filter">
          {["all", "scheduled", "live", "ended"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`status-filter-${s}`}
              className={`px-3 h-8 font-mono-ds text-[10px] uppercase tracking-widest ${
                statusFilter === s ? "bg-[#FF6A00] text-[#161616] font-bold" : "text-[#6E6E6E] hover:text-[#161616]"
              }`}
            >
              {s} <span className="ml-1 opacity-60">{s === "all" ? lives.length : lives.filter((l) => l.status === s).length}</span>
            </button>
          ))}
        </div>
        <div className="inline-flex border border-[#E5E1D8] bg-[#FFFFFF] p-1" data-testid="live-source-filter">
          {[
            { v: "all", l: "All" },
            { v: "handpicked", l: "Handpicked" },
            { v: "vault", l: "From Vault" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setSourceFilter(s.v)}
              className={`px-3 h-8 font-mono-ds text-[10px] uppercase tracking-widest ${
                sourceFilter === s.v ? "bg-[#FF6A00] text-[#161616] font-bold" : "text-[#6E6E6E] hover:text-[#161616]"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto">
        <table className="w-full text-sm font-mono-ds min-w-[1100px]">
          <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Source</th>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4">Stock</th>
              <th className="text-left p-4">Scheduled</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Joined</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#6E6E6E]">// No drops match this filter</td></tr>
            )}
            {visible.map((l) => (
              <tr key={l.live_id} className="border-b border-[#FFFFFF] hover:bg-[#FFFFFF]/70" data-testid={`live-row-${l.live_id}`}>
                <td className="p-4 text-[#161616]">{l.title}</td>
                <td className="p-4">
                  {l.auto_promoted ? (
                    <span className="brand-pill">
                      From Vault
                    </span>
                  ) : (
                    <span className="border border-[#E5E1D8] text-[#6E6E6E] px-1.5 py-0.5 font-mono-ds text-[9px] uppercase tracking-widest">
                      ★ Handpicked
                    </span>
                  )}
                </td>
                <td className="p-4 text-[#6E6E6E]">{l.product?.name || "—"}</td>
                <td className="p-4">
                  <span className={(l.product?.stock ?? 0) <= 0 ? "text-[#FF6A00] font-bold" : (l.product?.stock ?? 0) <= 10 ? "text-[#FF6A00]" : "text-[#161616]"}>
                    {l.product?.stock ?? 0}
                  </span>
                </td>
                <td className="p-4 text-[#6E6E6E] text-[10px]">{new Date(l.scheduled_at).toLocaleString("en-IN")}</td>
                <td className="p-4">
                  <span className={`uppercase text-[10px] tracking-widest px-2 py-1 border ${
                    l.status === "live" ? "border-[#FF6A00] text-[#FF6A00]" :
                    l.status === "ended" ? "border-[#E5E1D8] text-[#6E6E6E]" :
                    "border-[#E5E1D8] text-[#6E6E6E]"
                  }`}>{l.status}</span>
                </td>
                <td className="p-4 text-[#161616]">{l.participants}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  {l.status === "live" && (
                    <button onClick={() => setStatus(l.live_id, "end")} className="text-[#FF6A00] hover:text-[#161616] uppercase text-xs tracking-widest mr-3" data-testid={`end-live-${l.live_id}`}>
                      End
                    </button>
                  )}
                  <Link to={`/admin/live/${l.live_id}`} className="text-[#FF6A00] hover:text-[#161616] uppercase text-xs tracking-widest mr-3" data-testid={`enter-live-${l.live_id}`}>
                    {l.status === "live" ? "Host ◆" : "Go Live"}
                  </Link>
                  <Link to={`/admin/live/${l.live_id}?role=cohost`} className="text-[#264733] hover:text-[#161616] uppercase text-xs tracking-widest mr-3" data-testid={`cohost-live-${l.live_id}`} title="Join as Co-Host (chat moderation only, no video)">
                    ★ Co-Host
                  </Link>
                  <button className="text-[#6E6E6E] hover:text-[#FF6A00] uppercase text-xs tracking-widest mr-3" onClick={() => { setEdit(l); setOpen(true); }} data-testid={`edit-live-${l.live_id}`}>Edit</button>
                  <button className="text-[#6E6E6E] hover:text-[#FF6A00] uppercase text-xs tracking-widest" onClick={() => remove(l.live_id)} data-testid={`delete-live-${l.live_id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LiveDialog open={open} setOpen={setOpen} edit={edit} products={products} cohort={cohort} onSaved={load} />
    </AdminShell>
  );
}

function LiveDialog({ open, setOpen, edit, products, cohort, onSaved }) {
  const thresholds = cohort?.thresholds || FALLBACK_VIEWER_MILESTONES;
  const defaultMs = computeMilestones(2499, 799, thresholds);
  const [form, setForm] = useState({
    product_id: "", title: "", description: "",
    scheduled_at: defaultSchedule(),
    duration_seconds: 120,
    milestones: defaultMs,
    preshow_video_url: "",
  });
  const [autoSync, setAutoSync] = useState(true); // recompute when product changes

  useEffect(() => {
    if (edit) {
      setForm({
        product_id: edit.product_id,
        title: edit.title,
        description: edit.description || "",
        scheduled_at: new Date(edit.scheduled_at).toISOString().slice(0, 16),
        duration_seconds: edit.duration_seconds || 120,
        milestones: edit.milestones,
        preshow_video_url: edit.preshow_video_url || "",
      });
      setAutoSync(false); // editing → assume the existing curve is intentional
    } else {
      const firstProd = products[0];
      const starting = Number(firstProd?.starting_price || firstProd?.base_price || 2499);
      const floor = Number(firstProd?.final_price || starting * 0.5);
      setForm({
        product_id: firstProd?.product_id || "",
        title: "", description: "",
        scheduled_at: defaultSchedule(),
        duration_seconds: 120,
        milestones: computeMilestones(starting, floor, thresholds),
        preshow_video_url: "",
      });
      setAutoSync(true);
    }
  }, [edit, products, open, thresholds.join(",")]);

  // Selected product info (for the auto-engine preview)
  const selectedProduct = useMemo(
    () => products.find((p) => p.product_id === form.product_id),
    [products, form.product_id]
  );
  const starting = Number(selectedProduct?.starting_price || selectedProduct?.base_price || 0);
  const floor = Number(selectedProduct?.final_price || 0);

  // When product changes AND auto-sync is on, recompute the milestone curve
  const onPickProduct = (pid) => {
    setForm((f) => {
      const next = { ...f, product_id: pid };
      if (autoSync) {
        const p = products.find((x) => x.product_id === pid);
        if (p) {
          const s = Number(p.starting_price || p.base_price || 0);
          const fl = Number(p.final_price || s * 0.5);
          next.milestones = computeMilestones(s, fl, thresholds);
        }
      }
      return next;
    });
  };

  const resyncFromEngine = () => {
    if (!selectedProduct) return toast.error("Pick a product first");
    setForm((f) => ({ ...f, milestones: computeMilestones(starting, floor, thresholds) }));
    setAutoSync(true);
    toast.success("Milestones synced from price engine");
  };

  const updateMs = (i, key, val) => {
    const m = [...form.milestones];
    m[i] = { ...m[i], [key]: Number(val) };
    setForm({ ...form, milestones: m });
    setAutoSync(false); // any manual edit unlocks auto-sync
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return toast.error("Select a product");
    try {
      const payload = {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        milestones: form.milestones.map((m) => ({ threshold: Number(m.threshold), price: Number(m.price) })),
      };
      if (edit) await adminApi.put(`/lives/${edit.live_id}`, payload);
      else await adminApi.post("/lives", payload);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FFFFFF] border border-[#E5E1D8] rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">{edit ? "Edit Drop" : "New Drop"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 font-mono-ds text-sm" data-testid="live-form">
          <Field label="Product">
            <select required value={form.product_id} onChange={(e) => onPickProduct(e.target.value)}
              className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" data-testid="live-product-select">
              <option value="">Select…</option>
              {products.map((p) => (
                <option key={p.product_id} value={p.product_id}>
                  {`${p.name} (₹${p.starting_price || p.base_price} → ₹${p.final_price || "?"})`}
                </option>
              ))}
            </select>
            {selectedProduct && (
              <div className="mt-1 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
                Starting <span className="text-[#161616]">₹{starting}</span> · Floor <span className="text-[#FF6A00]">₹{floor}</span> · Total drop ₹{(starting - floor).toLocaleString("en-IN")}
              </div>
            )}
          </Field>
          <Field label="Title">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" data-testid="live-title-input" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" />
          </Field>
          <Field label="Pre-show video URL (optional)">
            <input
              type="url"
              placeholder="https://cdn.example.com/preshow.mp4"
              value={form.preshow_video_url}
              onChange={(e) => setForm({ ...form, preshow_video_url: e.target.value })}
              className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
              data-testid="live-preshow-input"
            />
            <div className="mt-1 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
              A short muted looping clip shown on the Home hero before tonight&apos;s drop. Direct .mp4 / .webm URL.
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Scheduled at (all times in IST · drops go live at 21:00)">
              <input required type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" data-testid="live-schedule-input" />
              {(() => {
                const s = seasonSlotFor(form.scheduled_at);
                if (!s) return (
                  <div className="mt-1 font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E]" data-testid="live-season-hint">
                    // Outside Season One (Aug 15–29, 2026)
                  </div>
                );
                return (
                  <div className="mt-1 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold" data-testid="live-season-hint">
                    // Drop #{String(s.drop_no).padStart(3, "0")} · {s.category}
                  </div>
                );
              })()}
            </Field>
            <Field label="Duration (s)">
              <input required type="number" min="30" value={form.duration_seconds}
                onChange={(e) => setForm({ ...form, duration_seconds: parseInt(e.target.value, 10) || 120 })}
                className="w-full bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none" />
            </Field>
          </div>

          {/* Engine-driven milestone preview + override */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
                Price Engine · 5 Milestones {cohort ? `· ${cohort.cohort_emoji || ""} ${cohort.cohort_label || ""}` : ""}
              </label>
              <div className="flex items-center gap-3">
                {!autoSync && selectedProduct && (
                  <button type="button" onClick={resyncFromEngine} className="font-mono-ds text-[10px] uppercase tracking-widest text-[#FF6A00] hover:text-[#161616]" data-testid="resync-engine-btn">
                    ↻ Resync from engine
                  </button>
                )}
                <span className={`font-mono-ds text-[9px] uppercase tracking-widest px-2 py-0.5 border ${autoSync ? "border-[#FF6A00] text-[#FF6A00]" : "border-[#E5E1D8] text-[#6E6E6E]"}`} data-testid="autosync-badge">
                  {autoSync ? "Engine-synced" : "Manual override"}
                </span>
              </div>
            </div>

            {cohort && (
              <div className="mt-2 border border-[#264733]/40 bg-[#B9E641]/15 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="live-cohort-banner">
                <span className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold">
                  // Cohort thresholds · {cohort.active_audience?.toLocaleString?.("en-IN") ?? cohort.active_audience}/{cohort.cohort_cap?.toLocaleString?.("en-IN") ?? cohort.cohort_cap} members
                </span>
                <span className="font-mono-ds text-[10px] text-[#264733]" data-testid="live-cohort-thresholds">
                  {thresholds.join(" · ")} viewers
                </span>
              </div>
            )}

            {/* Live preview stepper (no viewers yet) */}
            <div className="mt-2">
              <MilestoneStepper
                milestones={form.milestones}
                viewers={0}
                title="Curve preview"
              />
            </div>

            {/* Manual override grid (read/write) */}
            <details className="mt-2 group">
              <summary className="cursor-pointer font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] hover:text-[#FF6A00] select-none">
                Manual override — edit threshold / price values
              </summary>
              <div className="mt-2 space-y-2">
                {form.milestones.map((m, i) => (
                  <div key={`ms-row-${i}-${m.threshold}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <input
                      type="number" min="0" value={m.threshold}
                      onChange={(e) => updateMs(i, "threshold", e.target.value)}
                      placeholder="viewers ≥"
                      className="bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                      data-testid={`ms-threshold-${i}`}
                    />
                    <input
                      type="number" min="0" step="0.01" value={m.price}
                      onChange={(e) => updateMs(i, "price", e.target.value)}
                      placeholder="price ₹"
                      className="bg-transparent border border-[#E5E1D8] px-3 py-2 focus:border-[#FF6A00] outline-none"
                      data-testid={`ms-price-${i}`}
                    />
                    <span className="font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E]">M{i + 1}</span>
                  </div>
                ))}
              </div>
            </details>
            <div className="mt-1 text-[10px] text-[#6E6E6E]">
              Thresholds auto-scale with the active cohort (Founding → Early Insiders → …). Prices auto-generate from Starting → Floor via the canonical 5-tier engine. Override only for a non-standard curve.
            </div>
          </div>

          <Button type="submit" className="w-full rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="save-live-btn">
            Save Drop
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function defaultSchedule() {
  // Today 9 PM
  const d = new Date();
  d.setHours(21, 0, 0, 0);
  if (d < new Date()) d.setDate(d.getDate() + 1);
  // datetime-local format: YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
